import { useAnalytics, AnalyticsProvider as LibraryAnalyticsProvider } from '@sixfold/app-analytics-framework';
import { useLanguage } from '@sixfold/localization-component';
import * as React from 'react';

import { Company, useIsPresentOnTransporeonPlatform } from '../../company';
import { TourCounts } from '../../company/containers/hooks';
import { Routes } from '../../routes';
import { useIsVisibilityAgentEnabled, useUserProfile } from '../../user';
import { EmbedDataContext } from '../embed_data_context';

/**
 * Context to signal when initial analytics properties have been set.
 * This prevents race conditions where events are tracked before properties are ready.
 */
const AnalyticsPropertiesReadyContext = React.createContext<boolean>(false);

export const useAnalyticsPropertiesReady = () => React.useContext(AnalyticsPropertiesReadyContext);

interface Props {
  company: Company;
  tourCounts: TourCounts;
}

export const AnalyticsCommonPropsReporter: React.FunctionComponent<Props> = ({
  company: {
    company_id: companyId,
    company_name: companyName,
    is_carrier: isCarrier,
    is_shipper: isShipper,
    is_test: isTest,
  },
  tourCounts,
}) => {
  const language = useLanguage();
  const analytics = useAnalytics();
  const user = useUserProfile(companyId);

  const userId = user?.user_id ?? 'N/A';
  const role = user?.company?.role ?? 'N/A';
  const isInternal = user?.is_internal ?? 'N/A';

  React.useEffect(() => {
    analytics?.setProperties({
      language,
      companyId,
      userId,
      isShipper: isShipper.toString(),
      isCarrier: isCarrier.toString(),
      company_id: companyId,
      companyName: companyName ?? '',
      user_id: userId,
      is_shipper: isShipper.toString(),
      is_carrier: isCarrier.toString(),
      role_in_company: role,
      isTest: isTest.toString(),
      is_internal: isInternal.toString(),
      is_ocean_user: (tourCounts.ocean > 0).toString(),
      is_air_user: (tourCounts.air > 0).toString(),
    });
  }, [analytics, language, companyId, companyName, userId, role, isCarrier, isShipper, isTest, isInternal, tourCounts]);

  React.useEffect(() => {
    const onResize = () => {
      analytics?.setProperties({
        window_width: window.innerWidth.toString(),
        window_height: window.innerHeight.toString(),
      });
    };

    onResize();
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [analytics]);

  // We don't render any DOM
  return null;
};

export const IntercomAnalyticsPropsReporter: React.FunctionComponent<Props> = ({ company, tourCounts }) => {
  const isOnTransporeon = useIsPresentOnTransporeonPlatform(company.company_id);
  const user = useUserProfile(company.company_id);
  const analytics = useAnalytics();

  React.useEffect(() => {
    analytics?.setIntercomProperties({
      userId: user?.user_id ?? undefined,
      company: {
        companyId: company.company_id,
        name: company.company_name ?? undefined,
        customAttributes: {
          is_shipper: company.is_shipper,
          is_carrier: company.is_carrier,
          is_test: company.is_test,
        },
      },
      customAttributes: {
        locale: user?.locale,
        role_in_company: user?.company?.role,
        is_shipper: company.is_shipper,
        is_carrier: company.is_carrier,
        is_test: company.is_test,
        is_internal: user?.is_internal,
        latest_company_id: company.company_id,
        latest_company_name: company.company_name,
        is_ocean_user: tourCounts.ocean > 0,
        is_air_user: tourCounts.air > 0,
        ...{
          ...(isOnTransporeon.loading
            ? {}
            : { latest_company_is_on_transporeon: isOnTransporeon.isPresentOnTransporeonPlatform }),
        },
      },
    });
  }, [
    analytics,
    user,
    company.company_id,
    company.company_name,
    company.is_carrier,
    company.is_shipper,
    isOnTransporeon,
    company.is_test,
    tourCounts,
  ]);

  // We don't render any DOM
  return null;
};

/**
 * Sets initial user-level analytics properties as early as possible.
 * This runs inside the LibraryAnalyticsProvider to set properties
 * before child components track events.
 *
 * Uses AnalyticsPropertiesReadyContext to signal when properties are set,
 * preventing race conditions where events are tracked before properties are ready.
 */
const InitialAnalyticsPropsReporter: React.FunctionComponent<React.PropsWithChildren> = ({ children }) => {
  const analytics = useAnalytics();
  const profile = useUserProfile();
  const language = useLanguage();
  const [propertiesReady, setPropertiesReady] = React.useState(false);

  const userId = profile?.user_id ?? 'N/A';
  const isInternal = profile?.is_internal ?? 'N/A';
  const hasProfileData = profile !== undefined;

  React.useEffect(() => {
    // Wait until analytics is initialized and profile data is loaded
    if (analytics === undefined || !hasProfileData) {
      return;
    }

    // Set user-level properties as early as possible
    // Company-level properties (is_shipper, is_carrier, etc.) will be set
    // by AnalyticsCommonPropsReporter once company data is loaded
    analytics.setProperties({
      language,
      userId,
      user_id: userId,
      is_internal: isInternal.toString(),
    });

    // Signal that initial properties are now set
    setPropertiesReady(true);
  }, [analytics, language, userId, isInternal, hasProfileData]);

  return (
    <AnalyticsPropertiesReadyContext.Provider value={propertiesReady}>
      {children}
    </AnalyticsPropertiesReadyContext.Provider>
  );
};

const IntercomLauncherVisibility: React.FunctionComponent = () => {
  const analytics = useAnalytics();
  const { embedConfig } = React.useContext(EmbedDataContext);
  const isVisibilityAgentEnabled = useIsVisibilityAgentEnabled();
  const showLauncher = embedConfig?.intercom_chatbot_enabled === true && isVisibilityAgentEnabled === false;

  React.useEffect(() => {
    analytics?.setIntercomProperties({ hideDefaultLauncher: !showLauncher });
  }, [analytics, showLauncher]);

  return null;
};

export const AnalyticsProvider: React.FunctionComponent<React.PropsWithChildren<{ consented: boolean }>> = ({
  consented,
  children,
}) => {
  const { embedConfig } = React.useContext(EmbedDataContext);
  const profile = useUserProfile();

  const routes = Object.values(Routes);
  const userId = profile?.user_id;
  const mixpanelToken = embedConfig?.mixpanel_token;
  const mixpanelProxyUrl = embedConfig?.mixpanel_proxy_url;
  const inspectletWid = embedConfig?.inspectlet_wid !== undefined ? Number(embedConfig.inspectlet_wid) : undefined;
  const intercomOptions: React.ComponentProps<typeof LibraryAnalyticsProvider>['intercomOptions'] =
    React.useMemo(() => {
      return {
        appId: embedConfig?.intercom_appid,
        shouldTrackCustomEvents: false,
        hideDefaultLauncher: true,
      };
    }, [embedConfig]);

  return (
    <LibraryAnalyticsProvider
      userId={userId}
      mixpanelToken={mixpanelToken}
      mixpanelProxyUrl={mixpanelProxyUrl}
      inspectletWid={inspectletWid}
      intercomOptions={intercomOptions}
      routes={routes}
      isTrackingAllowed={consented}>
      <IntercomLauncherVisibility />
      <InitialAnalyticsPropsReporter>{children}</InitialAnalyticsPropsReporter>
    </LibraryAnalyticsProvider>
  );
};
